import {
  CalendarDays,
  CircleAlert,
  CircleCheck,
  LoaderCircle,
  MessagesSquare,
  TriangleAlert,
  UserRound,
  X,
} from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import { Button } from '../../../ui/Button/Button'
import type {
  BookingCheck,
  StaffResolution,
} from '../../booking-checks/booking-checks-contracts'
import { BookingCheckCard } from '../../booking-checks/components/BookingCheckCard'
import { formatPhone } from '../../patients/patient-display'
import type { AppointmentChangeAvailability } from '../appointment-change-availability'
import type { AppointmentAction } from '../appointment-actions-contracts'
import {
  formatAppointmentSchedule,
  formatAppointmentTime,
  pmsPresentation,
  type AppointmentItem,
} from '../appointments-model'
import styles from '../AppointmentsPage.module.css'
import type { AppointmentActionState } from '../use-appointment-action'
import { AppointmentPmsBadge } from './AppointmentPmsBadge'
import { AppointmentStatusBadge } from './AppointmentStatusBadge'

function timestampValue(timestamp: number, appointment: AppointmentItem) {
  return formatAppointmentTime(timestamp, appointment.clinicTimeZone).text
}

export function AppointmentDetail({
  appointment,
  titleId,
  availability,
  actionState,
  nowMs,
  onAction,
  onClose,
  onOpenPatient,
  onOpenConversation,
  bookingCheck = null,
  onResolveBookingCheck,
  bookingCheckPending = null,
  bookingCheckFailed = false,
}: {
  appointment: AppointmentItem
  titleId: string
  availability: AppointmentChangeAvailability
  actionState: AppointmentActionState
  nowMs: number
  onAction: (action: AppointmentAction) => void
  onClose: () => void
  onOpenPatient?: () => void
  onOpenConversation?: () => void
  /** The clinic's Booking check for this appointment, when the watch raised one. */
  bookingCheck?: BookingCheck | null
  onResolveBookingCheck?: (resolution: StaffResolution) => void
  bookingCheckPending?: StaffResolution | null
  bookingCheckFailed?: boolean
}) {
  const [pending, setPending] = useState<AppointmentAction | null>(null)
  const confirmTitleId = useId()
  const confirmDescriptionId = useId()
  const noteId = useId()
  const safeButton = useRef<HTMLButtonElement>(null)
  const confirmButton = useRef<HTMLButtonElement>(null)
  const cancelButton = useRef<HTMLButtonElement>(null)
  const feedback = useRef<HTMLParagraphElement>(null)
  const returnFocusTo = useRef<AppointmentAction | null>(null)

  const schedule = formatAppointmentSchedule(appointment)
  const isFuture =
    appointment.scheduledAtMs !== null && appointment.scheduledAtMs > nowMs
  const canConfirm = isFuture && appointment.status === 'scheduled'
  const canCancel =
    isFuture &&
    (appointment.status === 'scheduled' || appointment.status === 'confirmed')
  const submitting = actionState.status === 'submitting'
  const enabled = availability.status === 'available'
  const pms = pmsPresentation(appointment, nowMs)
  const phone = formatPhone(appointment.patientPhone)
  const settled =
    actionState.status === 'succeeded' ||
    actionState.status === 'failed' ||
    actionState.status === 'reconciliation'

  // The safe choice is focused when the second confirmation opens; going back
  // returns focus to the action that opened it.
  useEffect(() => {
    if (pending) {
      safeButton.current?.focus()
    } else if (returnFocusTo.current) {
      ;(returnFocusTo.current === 'confirm'
        ? confirmButton.current
        : cancelButton.current
      )?.focus()
      returnFocusTo.current = null
    }
  }, [pending])

  // The result replaces the confirmation; focus follows it once, when it
  // arrives, so it is announced. Going back never moves focus to an old result.
  const focusFeedback = useRef(false)
  const showFeedback = settled && !pending
  useEffect(() => {
    if (settled) {
      focusFeedback.current = true
      setPending(null)
    }
  }, [settled])
  useEffect(() => {
    if (showFeedback && focusFeedback.current) {
      focusFeedback.current = false
      feedback.current?.focus()
    }
  }, [showFeedback])

  function goBack() {
    returnFocusTo.current = pending
    setPending(null)
  }

  function submit() {
    if (!enabled || !pending || submitting) return
    onAction(pending)
  }

  const when = schedule ? `${schedule.date} at ${schedule.time}` : null

  return (
    <>
      <header className={styles.detailHeader}>
        <div className={styles.detailTitle}>
          <span className={styles.eyebrow}>Appointment</span>
          <h2 id={titleId}>{appointment.patientDisplayName}</h2>
          <div className={styles.detailBadges}>
            <AppointmentStatusBadge appointment={appointment} />
            <AppointmentPmsBadge appointment={appointment} nowMs={nowMs} />
          </div>
        </div>
        <button
          className={styles.modalClose}
          type="button"
          aria-label="Close details"
          disabled={submitting}
          onClick={onClose}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </header>

      <div className={styles.detailBody}>
        <section className={styles.scheduleSummary} aria-label="Scheduled time">
          <span className={styles.scheduleIcon} aria-hidden="true">
            <CalendarDays size={18} />
          </span>
          {schedule ? (
            <div>
              <strong>{schedule.date}</strong>
              <span>
                {schedule.time} · {schedule.timeZone}
              </span>
            </div>
          ) : (
            <div>
              <strong>Time needs review</strong>
              <span>
                {appointment.scheduledWallTime
                  ? `Recorded as ${appointment.scheduledWallTime}, without a time zone`
                  : 'The recorded time couldn’t be read'}
              </span>
            </div>
          )}
        </section>

        {bookingCheck ? (
          <BookingCheckCard
            check={bookingCheck}
            showAppointment={false}
            onResolve={onResolveBookingCheck}
            pending={bookingCheckPending}
            failed={bookingCheckFailed}
          />
        ) : pms?.needsAttention ? (
          <div className={styles.detailNotice} data-tone="warning">
            <TriangleAlert size={16} aria-hidden="true" />
            <p>
              <strong>Not in the practice system yet.</strong> Flio booked this
              visit, but the practice system hasn’t confirmed it. Check there
              before booking it again.
            </p>
          </div>
        ) : null}

        <dl className={styles.detailGrid}>
          <div>
            <dt>Visit type</dt>
            <dd>{appointment.appointmentTypeName ?? 'Not recorded'}</dd>
          </div>
          <div>
            <dt>Provider</dt>
            <dd>{appointment.providerName ?? 'Not recorded'}</dd>
          </div>
          <div>
            <dt>Room</dt>
            <dd>{appointment.operatoryName ?? 'Not recorded'}</dd>
          </div>
          {phone ? (
            <div>
              <dt>Patient phone</dt>
              <dd>{phone}</dd>
            </div>
          ) : null}
          <div>
            <dt>Source</dt>
            <dd>{appointment.sourceLabel}</dd>
          </div>
          {pms !== null ? (
            <div>
              <dt>Practice system</dt>
              <dd>{pms.label}</dd>
            </div>
          ) : null}
          {appointment.bookedAtMs !== null ? (
            <div>
              <dt>Booked</dt>
              <dd>{timestampValue(appointment.bookedAtMs, appointment)}</dd>
            </div>
          ) : null}
          {appointment.confirmedAtMs !== null ? (
            <div>
              <dt>Confirmed</dt>
              <dd>{timestampValue(appointment.confirmedAtMs, appointment)}</dd>
            </div>
          ) : null}
          {appointment.cancelledAtMs !== null ? (
            <div>
              <dt>Cancelled</dt>
              <dd>{timestampValue(appointment.cancelledAtMs, appointment)}</dd>
            </div>
          ) : null}
          {appointment.cancellationReason ? (
            <div className={styles.detailWide}>
              <dt>Cancellation reason</dt>
              <dd>{appointment.cancellationReason}</dd>
            </div>
          ) : null}
        </dl>
      </div>

      <footer className={styles.detailFooter}>
        {showFeedback ? (
          <p
            ref={feedback}
            tabIndex={-1}
            className={styles.actionFeedback}
            data-tone={
              actionState.status === 'succeeded'
                ? 'success'
                : actionState.status === 'reconciliation'
                  ? 'warning'
                  : 'danger'
            }
            role={actionState.status === 'succeeded' ? 'status' : 'alert'}
          >
            {actionState.status === 'succeeded' ? (
              <CircleCheck size={16} aria-hidden="true" />
            ) : (
              <CircleAlert size={16} aria-hidden="true" />
            )}
            <span>{actionState.message}</span>
          </p>
        ) : null}

        {pending ? (
          <div
            className={styles.confirmPanel}
            data-tone={pending === 'cancel' ? 'danger' : 'info'}
            role="alertdialog"
            aria-labelledby={confirmTitleId}
            aria-describedby={confirmDescriptionId}
          >
            <strong id={confirmTitleId}>
              {pending === 'confirm'
                ? 'Confirm this appointment?'
                : 'Cancel this appointment?'}
            </strong>
            <p id={confirmDescriptionId}>
              {pending === 'confirm'
                ? `${appointment.patientDisplayName}${when ? `, ${when}` : ''}. This marks the visit as confirmed in your practice system.`
                : `This cancels ${appointment.patientDisplayName}’s visit${when ? ` on ${when}` : ''} in your practice system.`}
            </p>
            <div className={styles.confirmActions}>
              <Button
                ref={safeButton}
                type="button"
                variant="secondary"
                disabled={submitting}
                onClick={goBack}
              >
                {pending === 'confirm' ? 'Go back' : 'Keep appointment'}
              </Button>
              <Button
                type="button"
                className={pending === 'cancel' ? styles.dangerButton : ''}
                disabled={!enabled || submitting}
                aria-busy={submitting || undefined}
                onClick={submit}
              >
                {submitting ? (
                  <>
                    <LoaderCircle
                      className={styles.buttonSpinner}
                      size={15}
                      aria-hidden="true"
                    />
                    {pending === 'confirm' ? 'Confirming…' : 'Cancelling…'}
                  </>
                ) : pending === 'confirm' ? (
                  'Confirm appointment'
                ) : (
                  'Cancel appointment'
                )}
              </Button>
            </div>
          </div>
        ) : canConfirm || canCancel ? (
          <section
            className={styles.manageRow}
            aria-label="Appointment actions"
          >
            {availability.status !== 'available' ? (
              <p id={noteId} className={styles.manageNote}>
                {availability.message}
              </p>
            ) : null}
            <div className={styles.manageButtons}>
              {canCancel ? (
                <Button
                  ref={cancelButton}
                  type="button"
                  variant="secondary"
                  className={styles.dangerOutline}
                  disabled={!enabled}
                  aria-describedby={enabled ? undefined : noteId}
                  onClick={() => setPending('cancel')}
                >
                  Cancel appointment
                </Button>
              ) : null}
              {canConfirm ? (
                <Button
                  ref={confirmButton}
                  type="button"
                  disabled={!enabled}
                  aria-describedby={enabled ? undefined : noteId}
                  onClick={() => setPending('confirm')}
                >
                  Confirm appointment
                </Button>
              ) : null}
            </div>
          </section>
        ) : null}

        {onOpenPatient || onOpenConversation ? (
          <div className={styles.detailLinks}>
            {onOpenPatient ? (
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={onOpenPatient}
              >
                <UserRound size={15} aria-hidden="true" /> Open patient
              </Button>
            ) : null}
            {onOpenConversation ? (
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={onOpenConversation}
              >
                <MessagesSquare size={15} aria-hidden="true" /> Open
                conversation
              </Button>
            ) : null}
          </div>
        ) : null}
      </footer>
    </>
  )
}
